import "./subway.scss";
import "./subwayPopover.scss";

import { List } from "immutable";
import _ from "lodash";
import PropTypes from "prop-types";
import PubSub from "pubsub-js";
import React, { PureComponent } from "react";
import ImmutablePropTypes from "react-immutable-proptypes";
import { connect } from "react-redux";

import { withContainerContext } from "../../components/container/ContainerContext";
import EmptyState from "../../components/emptyState";
import { withScreenContext } from "../../components/screen/ScreenContext";
import SplitView from "../../components/splitView";
import { GLOBAL_FILTER_ID, SUBWAY_VIEW } from "../../constants/identifiers";
import interfaceMessages from "../../constants/interfaceMessages";
import interfaceUserActions from "../../constants/interfaceUserActions";
import PubSubTopics from "../../constants/pubSubTopics";
import Dimensions from "../../constants/styles/dimensions";
import relationshipViewSelectors from "../../features/relationshipView/selectors";
import { appStateOperations, appStateSelectors } from "../../modules/appState";
import batchOperations from "../../modules/batchOperations";
import { batchActions } from "../../modules/chainReducer";
import { definitionSelectors } from "../../modules/definition";
import { calendarSelectors } from "../../modules/definition/calendar";
import { isDateValid } from "../../modules/definition/calendar/dateFunctions";
import { colorSelectors } from "../../modules/definition/colors";
import { propertySelectors } from "../../modules/definition/properties";
import { referenceSelectors } from "../../modules/definition/references";
import { subwayViewSettingsOperations } from "../../modules/definition/subwayViewSettings";
import { typeSelectors } from "../../modules/definition/types";
import dropSelectors from "../../modules/dropSelectors";
import { filterSelectors } from "../../modules/filters";
import { itemOperations, itemSelectors } from "../../modules/items";
import { relationshipOperations, relationshipSelectors } from "../../modules/relationships";
import { sequenceOperations } from "../../modules/sequence";
import { uiStateOperations, uiStateSelectors } from "../../modules/uiState";
import { shouldAutoEditItems } from "../../modules/undoRedo";
import { immutableRecords, PropTypeShapes } from "../../types";
import { mergeDispatchToProps } from "../../utils/codeSplit";
import { onPasteItems } from "../../utils/copyPaste";
import hasAncestorOfClass from "../../utils/hasAncestorOfClass";
import { appSelectors } from "../app";
import { displayItemSelectors } from "../sharedSelectors/displayItems";
import { withWorkspaceContext } from "../workspace/WorkspaceContext";
import selectors from "./selectors";
import SubwayCanvasView from "./SubwayCanvasView";

class SubwayView extends PureComponent {
  constructor(props, context) {
    super(props, context);
    this.onNodeDrop = this.onNodeDrop.bind(this);
    this.onItemClick = this.onItemClick.bind(this);
    this.onCanvasClick = this.onCanvasClick.bind(this);
    this.onItemDoubleClick = this.onItemDoubleClick.bind(this);
    this.onCreateItem = this.onCreateItem.bind(this);
    this.requestViewBoundsForZoom = this.requestViewBoundsForZoom.bind(this);
    this.requestCanDropItems = this.requestCanDropItems.bind(this);
    this.onChangeDisplayOption = this.onChangeDisplayOption.bind(this);
    this.onThreadToggle = this.onThreadToggle.bind(this);
    this.onAddNodeToThread = this.onAddNodeToThread.bind(this);

    // copy paste
    this._pubSubPasteToken = null;
    this.onPasteMessageReceived = this.onPasteMessageReceived.bind(this);
    this.onPaste = this.onPaste.bind(this);
    this.processPastingItems = this.processPastingItems.bind(this);
  }

  componentDidMount() {
    this._pubSubPasteToken = PubSub.subscribe(PubSubTopics.paste, this.onPasteMessageReceived);
    this.props.workViewRegistration.registerWorkViewCreateItemCallback(this.props.viewId, this.onCreateItem);
  }

  componentWillUnmount() {
    PubSub.unsubscribe(this._pubSubPasteToken);
    this.props.workViewRegistration.unregisterWorkViewCreateItemCallback(this.props.viewId);
  }

  canCreateItemOfType(typeId) {
    return typeSelectors.getTypeIsAllowedInSequence(this.props.localStore, typeId);
  }

  processPastingItems(itemMapList) {
    const visibleSelectedIds = this.props.selectedItemIds.filter(i => this.props.visibleItemIds.contains(i));

    let lastVisibleSequenceIndex =
      visibleSelectedIds.size === 0
        ? this.props.globalSequenceIds.size
        : this.props.globalSequenceIds.indexOf(visibleSelectedIds.last());

    let extraActions = [];
    itemMapList.forEach(im => {
      lastVisibleSequenceIndex += 1;
      extraActions.push(sequenceOperations.moveItemsInSequence([im.get("id")], lastVisibleSequenceIndex));
    });

    return extraActions;
  }

  onPaste(e) {
    const {
      dispatchBatchActions,
      projectId,
      isActiveView,
      sequenceTypeIds,
      defaultType,
      typesById,
      propertiesById,
      colorsById,
      referencesById,
      itemsById
    } = this.props;
    dispatchBatchActions(
      onPasteItems(
        e,
        projectId,
        isActiveView,
        itemMap => this.canCreateItemOfType(itemMap.get("type")),
        this.processPastingItems,
        sequenceTypeIds,
        defaultType,
        typeSelectors.isDatedType,
        typesById,
        propertiesById,
        colorsById,
        referencesById,
        itemsById
      )
    );
  }

  onPasteMessageReceived(msg, clipboardData) {
    this.onPaste({ clipboardData });
  }

  onCreateItem(typeId) {
    // If this type is not allowed in sequence, then don't add it here.
    if (!this.canCreateItemOfType(typeId)) {
      return false;
    }
    // If we have no threads, then don't add it
    if (!this.props.data.threads.size) {
      return false;
    }

    // Otherwise, we can add it.
    // Do we want to add it after current selection?
    const threadId = this.props.data.threads.first().id;
    const visibleSelectedIds = this.props.selectedItemIds.filter(i => this.props.visibleItemIds.contains(i));
    if (visibleSelectedIds.size === 0) {
      this.props.onAddNodeToThread(threadId, typeId, null, this.props.viewId);
    } else {
      const lastVisibleIndex = this.props.globalSequenceIds.indexOf(visibleSelectedIds.last());
      this.props.onAddNodeToThread(threadId, typeId, lastVisibleIndex + 1, this.props.viewId);
    }
    return true;
  }

  requestCanDropItems(itemIds, _dropNodes, dropDate, hoverThread, hoverIndex, adjustItemEndWhenChangingStart) {
    if (!itemSelectors.getItemsAreAllowedInSequence(this.props.localStore, itemIds)) {
      return {
        canDrop: false,
        message: interfaceMessages.DRAG_DROP_DATE_ORDER_NOT_ALLOWED
      };
    }

    const displayItems = this.props.displayItems.filter(item => itemIds.includes(item.id));

    if (hoverThread) {
      const threadTypeId = this.props.data.threads.find(thread => thread.id === hoverThread).typeId;
      const itemTypeIds = _.uniq(displayItems.map(item => item.type).toArray());

      for (let i = 0; i < itemTypeIds.length; i++) {
        const allowed = relationshipSelectors.getSubjectTypeAllowsRelationshipsForItemIds(
          this.props.itemsById,
          this.props.allowedReferencesPerSubjectObjectTypes,
          threadTypeId,
          itemIds
        );

        if (!allowed) {
          return {
            canDrop: false,
            message: interfaceMessages.DRAG_DROP_RELATIONSHIP_NOT_ALLOWED
          };
        }
      }
    }

    let newDropDateString;
    for (let i = 0; i < displayItems.size; i++) {
      const item = displayItems.get(i);

      // If the drag will change the item's date, show it in the drag message
      if (isDateValid(dropDate) && isDateValid(item.startDate) && item.startDate.timestamp !== dropDate.timestamp) {
        if (dropDate.timestamp != item.startDate.timestamp) {
          newDropDateString = `Move to: ${dropDate.label}`;

          // Block the drop if any dates are locked
          if (item.isDateLocked) {
            return {
              canDrop: false,
              message: interfaceMessages.DRAG_DROP_LOCKED_DATES
            };
          }
        }
      }
    }

    // If !adjustItemEndWhenChangingStart, only allow the drop if it isn't later than dates other than start date
    if (!adjustItemEndWhenChangingStart && dropDate) {
      for (let i = 0; i < displayItems.size; i++) {
        const item = displayItems.get(i);
        if (item) {
          if (
            isDateValid(item.endDate) &&
            item.endDate.timestamp != item.startDate.timestamp &&
            dropDate.timestamp > item.endDate.timestamp
          ) {
            return {
              canDrop: false,
              message: interfaceMessages.DRAG_DROP_END_DATE_NOT_ALLOWED
            };
          }

          if (
            isDateValid(item.latestStartDate) &&
            item.latestStartDate.timestamp != item.startDate.timestamp &&
            dropDate.timestamp > item.latestStartDate.timestamp
          ) {
            return {
              canDrop: false,
              message: interfaceMessages.DRAG_DROP_LATEST_START_DATE_NOT_ALLOWED
            };
          }

          if (
            isDateValid(item.earliestEndDate) &&
            item.earliestEndDate.timestamp != item.startDate.timestamp &&
            dropDate.timestamp > item.earliestEndDate.timestamp
          ) {
            return {
              canDrop: false,
              message: interfaceMessages.DRAG_DROP_EARLIEST_END_DATE_NOT_ALLOWED
            };
          }
        }
      }
    }
    return { canDrop: true, message: newDropDateString ? newDropDateString : null };
  }

  onNodeDrop(itemIds, newThread, newIndex, movingLeft, fromExternalSource) {
    if (newThread) {
      this.props.onNewThread(itemIds, newThread, fromExternalSource, this.props.viewId);
    } else {
      const displayNodes = this.props.data.nodes;
      let globalSequenceIndex = null;

      if (!_.isNil(newIndex)) {
        // Need to find the sequenceIndex of the node at that actual index...
        const nodeAtIndex =
          newIndex < 0
            ? displayNodes.first()
            : newIndex >= displayNodes.size
              ? displayNodes.last()
              : displayNodes.find(n => n.sequenceIndex === newIndex);

        let action = null;
        if (nodeAtIndex) {
          globalSequenceIndex = this.props.globalSequenceIds.indexOf(nodeAtIndex.itemId) + (movingLeft ? 0 : 1);
          action = dropSelectors.getActionForSequenceDrop(
            this.props.localStore,
            itemIds,
            nodeAtIndex.itemId,
            globalSequenceIndex,
            false,
            this.props.adjustItemEndWhenChangingStart
          );
        }
        if (fromExternalSource) {
          action = batchOperations.matchFiltersAfterDropAction(action, itemIds, this.props.viewId);
        }
        this.props.onNodeDrop(action);
      }
    }
    //#!if iOS !== true
    this.props.onSelectItems(itemIds);
    //#!endif
  }

  requestViewBoundsForZoom(_zoom) {
    return this.props.data.rect;
  }

  onItemClick(itemId, modifiers, wasSelected) {
    //#!if iOS === false
    if (modifiers.CONTROL || modifiers.COMMAND || modifiers.SHIFT) {
      if (wasSelected) {
        this.props.onRemoveFromSelection(itemId);
      } else {
        this.props.onAddToSelection(itemId);
      }
    }
    //#!elseif iOS === true
// if (this.props.screenContext.isEditMode) {
// if (wasSelected) {
// this.props.onRemoveFromSelection(itemId);
// } else {
// this.props.onAddToSelection(itemId);
// }
// }
    //#!endif
    else {
      this.props.onSelectItems(itemId);
    }
  }

  onItemDoubleClick() {
    if (this.props.onShowInspector) {
      this.props.onShowInspector();
    }
  }

  onChangeDisplayOption(display) {
    this.props.onChangeDisplayOption(display);
  }

  onThreadToggle(itemId, showThread) {
    this.props.onThreadToggle(this.props.viewId, itemId, showThread);
  }

  onAddNodeToThread(threadId) {
    this.props.onAddNodeToThread(threadId, this.props.defaultType, null, this.props.viewId);
  }

  onCanvasClick(e) {
    const clickWasNode =
      hasAncestorOfClass(e.target, "subwayNode") ||
      hasAncestorOfClass(e.target, "subwayAddNode") ||
      hasAncestorOfClass(e.target, "subwayThreadLabelBox");
    if (!clickWasNode) {
      this.props.onClick();
    }
  }

  renderSplitView = () => {
    let splitProps;
    splitProps = {
      vertical: false,
      constraints: List([
        immutableRecords.SplitConstraintsRecord({
          minSize: Dimensions.MIN_WORKVIEW_SIZE,
          canResize: false,
          preferredSize: 1000
        })
      ])
    };

    const childComponents = [
      <SubwayCanvasView
        key="subwayCanvasView"
        data={this.props.data}
        visibleItemIds={this.props.visibleItemIds}
        selectedItemIds={this.props.selectedItemIds}
        fontSize={this.props.fontSize}
        subwayViewSettings={this.props.subwayViewSettings}
        viewId={this.props.viewId}
        isActiveView={this.props.isActiveView}
        allowedThreads={this.props.allowedThreads}
        onChangeDisplayOption={this.onChangeDisplayOption}
        onThreadToggle={this.onThreadToggle}
        onAddThread={this.props.onAddThread}
        onAddNode={this.onAddNodeToThread}
        onNodeDrop={this.onNodeDrop}
        onItemClick={this.onItemClick}
        onItemDoubleClick={this.onItemDoubleClick}
        onSelectItems={this.props.onSelectItems}
        onLabelChange={this.props.onLabelChange}
        requestViewBoundsForZoom={this.requestViewBoundsForZoom}
        requestCanDropItems={this.requestCanDropItems}
        autoEditBlankNodes={this.props.autoEditBlankNodes}
        adjustItemEndWhenChangingStart={this.props.adjustItemEndWhenChangingStart}
        displayItems={this.props.displayItems}
        defaultType={this.props.defaultType}
        typesById={this.props.typesById}
      />
    ];

    return (
      <SplitView key="subwayLayoutSplitView" {...splitProps}>
        {childComponents}
      </SplitView>
    );
  };

  render() {
    let emptyState = null;

    if (!this.props.data.threads.size) {
      //#!if readonly === true
      const isFiltered = this.props.isViewFilterActive || this.props.isGlobalFilterActive;
      emptyState = (
        <EmptyState type="text" absolute={true}>
          <div key="content">
            <p>No items to display{isFiltered ? " for this filter" : ""}.</p>
          </div>
        </EmptyState>
      );
      //#!else
// emptyState = <EmptyState view={SUBWAY_VIEW} type="view" absolute={true} />;
      //#!endif
    }

    return (
      <div className="subwayView" onClick={this.onCanvasClick} onPaste={this.onPaste}>
        {this.renderSplitView()}
        {emptyState}
      </div>
    );
  }
}

const mapStateToProps = (state, props) => {
  const data = selectors.getSubwayViewData(state.present, props.viewId);
  const visibleItemIds = selectors.getVisibleSubwayIdsForView(state.present, props.viewId);
  const allowedThreads = selectors.getAllowedThreadsForView(state.present, props.viewId);
  const allowedReferencesPerSubjectObjectTypes = relationshipViewSelectors.getAllowedReferencesPerSubjectObjectTypes(
    state.present
  );
  const displayItems = displayItemSelectors.getDisplayItemsList(state.present);

  return {
    data,
    globalSequenceIds: state.present.data.sequence,
    visibleItemIds,
    allowedThreads,
    selectedItemIds: uiStateSelectors.getSelectedItems(state.present),
    subwayViewSettings: definitionSelectors.getSubwayViewSettings(state.present),
    fontSize: appStateSelectors.getFontSize(state.present),
    defaultType: selectors.getSubwayViewDefaultType(state.present),
    sequenceTypeIds: typeSelectors.getSequenceTypeIds(state.present),
    itemsById: itemSelectors.getItemsById(state.present),
    typesById: definitionSelectors.getTypesById(state.present),
    propertiesById: propertySelectors.getPropertiesById(state.present),
    colorsById: colorSelectors.getColorsById(state.present),
    referencesById: referenceSelectors.getReferencesById(state.present),
    calendar: calendarSelectors.getCalendar(state.present),
    adjustItemEndWhenChangingStart: definitionSelectors.getAdjustItemEndWhenChangingStart(state.present),
    // Save access to full tree for Drag Preview Data
    localStore: state.present,
    projectId: appSelectors.getProjectId(state.present),
    isActiveView: appStateSelectors.isViewActive(state.present, props.viewId),
    allowedReferencesPerSubjectObjectTypes,
    displayItems,
    isGlobalFilterActive: filterSelectors.isFilterWithIdActive(state.present, GLOBAL_FILTER_ID),
    isViewFilterActive: filterSelectors.isFilterWithIdActive(state.present, props.viewId),
    // Disable auto-edit when applying a REDO action
    autoEditBlankNodes: shouldAutoEditItems(state)
  };
};

const readonlyActions = {
  onSelectItems: appStateOperations.selectItems,
  onAddToSelection: appStateOperations.addItemsToSelection,
  onRemoveFromSelection: appStateOperations.removeItemsFromSelection,
  onClick: appStateOperations.clearSelection,
  onChangeDisplayOption: subwayViewSettingsOperations.changeDisplay,
  onShowInspector: uiStateOperations.showInspectorModal,
  onThreadToggle: (viewId, itemId, showThread) => {
    const actions = [appStateOperations.toggleSubwayThread(viewId, itemId, showThread)];
    //#!if readonly !== true
// actions.push(appStateOperations.userInterfaceActionPerformed(interfaceUserActions.TOGGLE_SUBWAY_TRACK));
    //#!endif
    return batchActions(actions);
  }
};

const editingActions = {
  onAddThread: batchOperations.createItemAndAddSubwayThread,
  onAddNodeToThread: (threadId, typeId, sequenceIndex, viewId) => {
    if (_.isNil(sequenceIndex)) {
      return batchOperations.matchFiltersAfterCreateAction(
        batchOperations.createItemWithRelationship(typeId, threadId),
        viewId
      );
    } else {
      return batchOperations.matchFiltersAfterCreateAction(
        batchOperations.createItemWithRelationshipInSequence(typeId, threadId, sequenceIndex),
        viewId
      );
    }
  },
  onNewThread: (itemIds, newThread, fromExternalSource, viewId) => {
    if (newThread) {
      let action = relationshipOperations.createRelationship(itemIds, null, newThread);
      if (fromExternalSource) {
        action = batchOperations.matchFiltersAfterDropAction(action, itemIds, viewId);
      }
      return action;
    }
  },
  onNodeDrop: a => {
    return a;
  },
  onLabelChange: itemOperations.editLabel,
  dispatchBatchActions: actions => {
    return batchOperations.reBatchActions(actions);
  },
  onShowInspector: appStateOperations.showRightPanel
};

const mapDispatchToProps = dispatch => mergeDispatchToProps(readonlyActions, editingActions, dispatch);

SubwayView.propTypes = {
  data: PropTypeShapes.subwayData.isRequired,
  globalSequenceIds: ImmutablePropTypes.listOf(PropTypes.string).isRequired,
  visibleItemIds: ImmutablePropTypes.listOf(PropTypes.string).isRequired,
  viewId: PropTypes.string.isRequired,
  projectId: PropTypes.string.isRequired,
  isActiveView: PropTypes.bool.isRequired,
  fontSize: PropTypes.number.isRequired,
  sequenceTypeIds: ImmutablePropTypes.listOf(PropTypes.string).isRequired,
  defaultType: PropTypes.string.isRequired,
  // onEventDrop: PropTypes.func.isRequired,
  // onZoom: PropTypes.func.isRequired,
  subwayViewSettings: PropTypeShapes.subwayViewSettings.isRequired,
  allowedThreads: PropTypeShapes.listOptionGroupList.isRequired,
  selectedItemIds: ImmutablePropTypes.listOf(PropTypes.string).isRequired,
  onSelectItems: PropTypes.func.isRequired,
  onAddToSelection: PropTypes.func.isRequired,
  onRemoveFromSelection: PropTypes.func.isRequired,
  onClick: PropTypes.func.isRequired,
  onChangeDisplayOption: PropTypes.func.isRequired,
  onThreadToggle: PropTypes.func.isRequired,
  onAddThread: PropTypes.func.isRequired,
  onAddNodeToThread: PropTypes.func.isRequired,
  onNodeDrop: PropTypes.func.isRequired,
  onNewThread: PropTypes.func.isRequired,
  onLabelChange: PropTypes.func.isRequired,
  autoEditBlankNodes: PropTypes.bool.isRequired,
  adjustItemEndWhenChangingStart: PropTypes.bool.isRequired,
  displayItems: ImmutablePropTypes.list.isRequired,
  localStore: PropTypes.object.isRequired,
  itemsById: ImmutablePropTypes.map.isRequired,
  calendar: PropTypeShapes.calendar.isRequired,
  allowedReferencesPerSubjectObjectTypes: ImmutablePropTypes.map,
  isViewFilterActive: PropTypes.bool,
  isGlobalFilterActive: PropTypes.bool,

  // copy paste
  dispatchBatchActions: PropTypes.func,
  typesById: ImmutablePropTypes.map,
  propertiesById: ImmutablePropTypes.map,
  colorsById: ImmutablePropTypes.map,
  referencesById: ImmutablePropTypes.map,

  onShowInspector: PropTypes.func,

  // Workspace context
  workViewRegistration: PropTypes.object.isRequired,

  // From withContainerContext
  containerRect: PropTypeShapes.flatRect.isRequired,

  //#!if iOS === true
  // From withScreenContext
// screenContext: PropTypes.object
  //#!endif
};

const normalView = withWorkspaceContext(connect(mapStateToProps, mapDispatchToProps)(SubwayView));
let exportedView = withContainerContext(normalView);
//#!if iOS === true
// exportedView = withScreenContext(exportedView);
//#!endif

export default exportedView;
